import * as React from "react";
import styled from "styled-components";
import { select } from "@hubper/theme";
import { Icon, Spinner } from "../../elements";
import TimeBar from "./TimeBar";

const Base = styled.div`
  display: flex;
`;

const LoaderWrapper = styled.div`
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Button = styled.div`
  transition: 0.2s;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${select.color(["grey", 2])};
  background-color: white;
  cursor: pointer;

  & > * {
    fill: ${select.color(["grey", 3])};
  }

  &:hover {
    transform: scale(1.04);

    box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.15);

    & > * {
      fill: ${select.color(["grey", 5])};
    }
  }

  &:active {
    transform: scale(1.02);
    box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0);
  }
`;

type Props = {
  style?: React.CSSProperties;
  className?: string;
  audioUrl: string;
};

type PlaybackState = "play" | "pause";

type State = {
  loading: boolean;
  playbackStatus: PlaybackState;
  duration: number | null;
  currentTime: number;
};

function AudioPlayer({ style, className, audioUrl }: Props, ref: any) {
  const audioRef = React.useRef<HTMLAudioElement>(null);

  const [state, setState] = React.useState<State>({
    loading: true,
    playbackStatus: "pause",
    duration: null,
    currentTime: 0
  });

  React.useEffect(() => {
    if (!state.loading) {
      setState({ ...state, loading: true });
    }

    function onAutoLoad() {
      try {
        setState({
          ...state,
          duration: audioRef.current!.duration,
          loading: false
        });

        if (state.playbackStatus === "play") {
          audioRef.current!.play();
        }
      } catch (e) {
        console.error(e);
      }
    }

    audioRef.current!.addEventListener("loadeddata", onAutoLoad, true);
    audioRef.current!.setAttribute("src", audioUrl);
    audioRef.current!.load();

    return () =>
      audioRef.current!.removeEventListener("loadeddata", onAutoLoad);
  }, [audioUrl]);

  function togglePlayback() {
    if (state.playbackStatus !== "play") {
      audioRef.current!.play();
    } else {
      audioRef.current!.pause();
    }
    setState({
      ...state,
      playbackStatus: state.playbackStatus === "play" ? "pause" : "play"
    });
  }

  return (
    <div ref={ref} style={style} className={className}>
      <audio
        ref={audioRef}
        onTimeUpdate={() =>
          setState(s => ({ ...s, currentTime: audioRef.current!.currentTime }))
        }
        hidden
      >
        Your browser does not support the audio element.
      </audio>
      {!state.loading ? (
        <Base>
          <Button
            onClick={evt => {
              evt.preventDefault();
              evt.stopPropagation();
              togglePlayback();
            }}
          >
            <Icon
              c="inherit"
              size={24}
              name={state.playbackStatus === "pause" ? "video" : "pause"}
            />
          </Button>

          <TimeBar
            currentTime={state.currentTime}
            duration={state.duration || 1}
            onTimeChange={seconds => {
              audioRef.current!.currentTime = seconds;
            }}
          />
        </Base>
      ) : (
        <LoaderWrapper>
          <Spinner />
        </LoaderWrapper>
      )}
    </div>
  );
}

export default React.forwardRef(AudioPlayer);
